import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import logoMedical from "../../assets/logo-medical.png";
import { useGameTexts } from "../../hooks/useGameTexts";
import { SERVER_URL } from "../../api";
import { ROUTES } from "../../utils/constants";
import { getScenarioByScreen, handleNavigate } from "../../utils/helpers";
import { modalGeneric } from "../../utils/logger";

const DEFAULT_TITLE = "Uno de los fármacos que puedes introducir para la triple terapia es <span class=\"txt-yellow-light\">voclosporina</span>.";
const DEFAULT_SUBTITLE = "¿Has oído hablar de este nuevo inhibidor de la calcineurina? ¿Y del estudio Aurora 1?";
const DEFAULT_CAPTION = "Figura adaptada de Huizinga et al. The 12th International Congress on Systemic Lupus Erythematosus (LUPUS 2017) & the 7th Asian Congress on Autoimmunity (ACA 2017).";
const DEFAULT_IMAGE_CITATION = "<sup>1</sup>Busque S et al. Am J Transplant 2011;11:2675–84. Rovin BH et al. Data presented at American Society of Nephrology Kidney Week 2020 Reimagined virtual conference, 19–25 October 2020. Kuglstatter A et al. Acta Crystallogr D Biol Crystallogr. 2011;67:119‒23. Van Gelder T et al. Expert Rev Clin Pharmacol. 2022;15:515‒29.";
const DEFAULT_MODAL = "<p style=\"text-align:center;\"><span style=\"color:hsl(210, 75%, 60%);\"><strong>El estudio Aurora 1<sup>1</sup> es un ensayo clínico aleatorizado, multicéntrico, controlado por placebo y doble ciego, N = 357, cuya variable principal fue respuesta renal completa a la semana 52 con n=357 pacientes con nefritis lúpica.</strong></span></p><p style=\"text-align:center;\">A continuación, encontrarás una batería de 4 afirmaciones.</p><p style=\"text-align:center;\"><strong>¿Son verdaderas o falsas?</strong></p><p><span style=\"font-size:12px;\"><sup>1</sup> Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80.</span></p>";

const MODAL_POINTS_HTML = "<div class=\"row mt-4 d-flex align-items-center px-5\"><div class=\"col-6 text-left\"><div>Acertarlas todas en los 3 primeros intentos:</div></div><div class=\"col-6 d-flex justify-content-end\"><div class=\"py-2 w-50 bg-correct txt-black bold d-flex align-items-center justify-content-center\">+40 puntos</div></div></div><div class=\"row mt-2 d-flex align-items-center px-5\"><div class=\"col-6 text-left\"><div>En otro caso:</div></div><div class=\"col-6 d-flex justify-content-end\"><div class=\"py-2 w-50 bg-incorrect txt-black bold d-flex align-items-center justify-content-center\">0 puntos</div></div></div>";

function buildImageUrl(value) {
	if (!value || typeof value !== "string") return null;
	if (value.startsWith("http")) return value;
	return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function Scenario5Part2Intro2({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);
	const { texts, ready } = useGameTexts(group?.game);
	const [imageLoaded, setImageLoaded] = useState(false);

	const title = texts["scenario5.2.intro2.title"] ?? DEFAULT_TITLE;
	const subtitle = texts["scenario5.2.intro2.subtitle"] ?? DEFAULT_SUBTITLE;
	const caption = texts["scenario5.2.intro2.caption"] ?? DEFAULT_CAPTION;
	const imageCitation = texts["scenario5.2.intro2.imageCitation"] ?? DEFAULT_IMAGE_CITATION;
	const modalContent = texts["scenario5.2.intro2.modalContent"] ?? DEFAULT_MODAL;
	const imageUrl = texts["scenario5.2.intro2.image"] ? buildImageUrl(texts["scenario5.2.intro2.image"]) : null;

	// Imagen: solo mostrar cuando esté cargada (se precarga en la escena anterior)
	useEffect(() => {
		if (!imageUrl) {
			setImageLoaded(false);
			return;
		}
		setImageLoaded(false);
		const img = new Image();
		let cancelled = false;
		img.onload = () => { if (!cancelled) setImageLoaded(true); };
		img.onerror = () => { if (!cancelled) setImageLoaded(false); };
		img.src = imageUrl;
		return () => { cancelled = true; img.src = ""; };
	}, [imageUrl]);

	const explanationHtml = (modalContent || DEFAULT_MODAL) + MODAL_POINTS_HTML;

	return (
		<div className="h-100">
			<div className="container-fluid bg-internas">
				<div style={{ paddingTop: "6vh", paddingLeft: "6vh" }}>
					<img width={240} src={logoMedical} alt="" />
				</div>
				<div
					style={{ paddingLeft: "6vh" }}
					className="row d-flex justify-content-start align-items-center pt-5"
				>
					{!ready ? (
						<>
							<div className="col-4 txtWhite mb-5">
								<div style={{ height: 28, width: "90%", background: "rgba(255,255,255,0.2)", borderRadius: 4 }} aria-hidden />
								<div style={{ height: 48, width: "100%", background: "rgba(255,255,255,0.15)", borderRadius: 4, marginTop: 12 }} aria-hidden />
								<div style={{ height: 72, width: "100%", background: "rgba(255,255,255,0.1)", borderRadius: 4, marginTop: 16 }} aria-hidden />
							</div>
							<div className="col-8">
								<div style={{ width: "95%", height: 350, background: "#e0e0e0", borderRadius: 8 }} aria-hidden />
							</div>
						</>
					) : (
						<>
					<div className="col-4 txtWhite mb-5">
						<div
							className="txtWhite txt20 bold mt-3"
							style={{ letterSpacing: 3 }}
							dangerouslySetInnerHTML={{ __html: title }}
						/>
						<div
							className="bold txt-yellow-light mt-3"
							style={{ lineHeight: "20px" }}
							dangerouslySetInnerHTML={{ __html: subtitle }}
						/>
						<div
							className="txt8 txt-white mt-5 pt-4"
							style={{ lineHeight: "15px" }}
							dangerouslySetInnerHTML={{ __html: caption }}
						/>
					</div>
					<div className="col-8">
						{imageUrl && imageLoaded ? (
							<img width="95%" src={imageUrl} alt="" style={{ display: "block" }} />
						) : (
							<div style={{ width: "95%", height: 350, background: "#e0e0e0", borderRadius: 8 }} aria-hidden />
						)}
						<div
							className="txt8 txt-white px-5 mt-4"
							style={{ lineHeight: "15px" }}
							dangerouslySetInnerHTML={{ __html: imageCitation }}
						/>
					</div>
						</>
					)}
					<div
						className="container"
						style={{
							position: "absolute",
							right: "7vh",
							bottom: "7vh",
						}}
					>
						<div className="d-flex justify-content-end">
							<div
								onClick={() =>
									modalGeneric({ html: explanationHtml }).then(
										(res) => {
											if (res.isConfirmed)
												handleNavigate({
													group: group,
													navigate: navigate,
													dispatch: dispatch,
													navigateTo:
														ROUTES.SCENARIO5_PART2,
													scenarioNum:
														getScenarioByScreen(
															ROUTES.SCENARIO5_PART2
														),
												});
										}
									)
								}
								className="btn-main"
							>
								<div className="text-btn-main">AVANZAR</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default injectIntl(Scenario5Part2Intro2);
